import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  getMembres, 
  getFamilles,
  getMontantsMensuels,
  getMontantsMensuelsFamilles,
  getPaiements,
  getPaiementsFamilles
} from '../services/api';
import { notificationService } from '../utils/notifications';
import DetailMembre from '../components/DetailMembre';
import Footer from '../components/Footer';
import './DashboardFinances.css';

const DashboardFinances = () => {
  const navigate = useNavigate();
  const finances = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('finances_user') || '{}');
    } catch {
      return {};
    }
  }, []);

  // Liste des membres individuels uniquement (les membres de famille sont exclus)
  const [membres, setMembres] = useState([]);
  const [familles, setFamilles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('membres'); // 'membres', 'familles', 'rapport'
  const [searchTerm, setSearchTerm] = useState('');
  const [searchTermFamilles, setSearchTermFamilles] = useState('');
  const [searchTermRapport, setSearchTermRapport] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [currentPageFamilles, setCurrentPageFamilles] = useState(1);
  const [currentPageRapport, setCurrentPageRapport] = useState(1);
  const [itemsPerPage] = useState(20);
  const [itemsPerPageRapport] = useState(10);
  const [selectedMembre, setSelectedMembre] = useState(null);
  const [selectedFamille, setSelectedFamille] = useState(null);
  const [selectedMembreDetail, setSelectedMembreDetail] = useState(null);

  // États pour les montants mensuels (utilisés pour le rapport journalier)
  const [montantsMensuels, setMontantsMensuels] = useState([]);
  const [montantsMensuelsFamilles, setMontantsMensuelsFamilles] = useState([]);
  const [paiementsJournaliers, setPaiementsJournaliers] = useState([]);
  const [paiementsFamillesJournaliers, setPaiementsFamillesJournaliers] = useState([]);
  const [paiementsJournaliersCombines, setPaiementsJournaliersCombines] = useState([]);
  const [loadingRapport, setLoadingRapport] = useState(false);
  
  // États pour le rapport des récolteurs
  const [showRapportRecolteurs, setShowRapportRecolteurs] = useState(false);
  const [rapportType, setRapportType] = useState('journalier'); // 'journalier' ou 'global'
  const [dateRapport, setDateRapport] = useState(new Date().toISOString().split('T')[0]);

  useEffect(() => {
    if (!finances.id) {
      navigate('/login-finances');
      return;
    }
    loadData();
  }, [finances, navigate]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [membresRes, famillesRes, montantsRes, montantsFamillesRes] = await Promise.all([
        getMembres(),
        getFamilles(),
        getMontantsMensuels(),
        getMontantsMensuelsFamilles()
      ]);

      // Filtrer les membres pour exclure les membres de famille et les chefs de famille
      // Ne garder que les membres individuels (type === 'individuel' ou pas de type/famille_id)
      const membresData = (membresRes.data || []).filter(membre => {
        // Exclure les membres qui ont un type 'famille' ou un famille_id
        // Exclure aussi les membres dont le numéro matricule commence par 'FAM-' (chefs de famille)
        const isMembreFamille = membre.type === 'famille' || 
                                 membre.famille_id || 
                                 (membre.numero_matricule && membre.numero_matricule.startsWith('FAM-'));
        return !isMembreFamille;
      });
      
      console.log(`✅ Membres filtrés: ${membresData.length} membres individuels (sur ${membresRes.data?.length || 0} total)`);
      setMembres(membresData);
      setFamilles(famillesRes.data || []);
      setMontantsMensuels(montantsRes.data || []);
      setMontantsMensuelsFamilles(montantsFamillesRes.data || []);
    } catch (error) {
      console.error('Erreur lors du chargement des données:', error);
      notificationService.error('Erreur', 'Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const loadRapportJournalier = async () => {
    try {
      setLoadingRapport(true);
      
      // Recharger les membres et familles pour avoir les données à jour
      const [membresRes, famillesRes, paiementsRes, paiementsFamillesRes] = await Promise.all([
        getMembres(),
        getFamilles(),
        getPaiements(),
        getPaiementsFamilles()
      ]);

      // Filtrer les membres pour exclure les membres de famille et les chefs de famille
      const membresData = (membresRes.data || []).filter(membre => {
        const isMembreFamille = membre.type === 'famille' || 
                                 membre.famille_id || 
                                 (membre.numero_matricule && membre.numero_matricule.startsWith('FAM-'));
        return !isMembreFamille;
      });
      const famillesData = famillesRes.data || [];
      const tousPaiements = paiementsRes.data || [];
      const tousPaiementsFamilles = paiementsFamillesRes.data || [];
      
      console.log(`📊 Total paiements membres récupérés: ${tousPaiements.length}`);
      console.log(`📊 Total paiements familles récupérés: ${tousPaiementsFamilles.length}`);

      // Filtrer les paiements confirmés uniquement par 'finances' (tous les paiements, pas seulement aujourd'hui)
      const paiementsConfirmesFinances = tousPaiements.filter(p => {
        // Si confirmed_by n'existe pas, ne pas inclure le paiement (seulement les paiements explicitement confirmés par finances)
        const hasConfirmedByField = 'confirmed_by' in p;
        return hasConfirmedByField && p.confirmed_by === 'finances';
      });
      
      console.log(`📊 Paiements membres confirmés par finances: ${paiementsConfirmesFinances.length}`);

      const paiementsFamillesConfirmesFinances = tousPaiementsFamilles.filter(p => {
        // Si confirmed_by n'existe pas, ne pas inclure le paiement (seulement les paiements explicitement confirmés par finances)
        const hasConfirmedByField = 'confirmed_by' in p;
        return hasConfirmedByField && p.confirmed_by === 'finances';
      });
      
      console.log(`📊 Paiements familles confirmés par finances: ${paiementsFamillesConfirmesFinances.length}`);

      // Enrichir avec les informations des membres et familles
      const paiementsAvecDetails = paiementsConfirmesFinances.map(paiement => {
        const membre = membresData.find(m => m.id === paiement.membre_id);
        // Priorité: relation recolteur.nom_complet > nom_recolteur stocké
        const nomRecolteur = membre?.recolteur?.nom_complet || membre?.nom_recolteur || null;
        return {
          ...paiement,
          membre: membre ? {
            nom: membre.nom,
            prenom: membre.prenom,
            postnom: membre.postnom,
            numero_matricule: membre.numero_matricule
          } : null,
          recolteur: nomRecolteur ? { nom_complet: nomRecolteur } : null
        };
      });

      const paiementsFamillesAvecDetails = paiementsFamillesConfirmesFinances.map(paiement => {
        const famille = famillesData.find(f => f.id === paiement.famille_id);
        // Priorité: relation recolteur.nom_complet > nom_recolteur stocké
        const nomRecolteur = famille?.recolteur?.nom_complet || famille?.nom_recolteur || null;
        return {
          ...paiement,
          famille: famille ? {
            nom_famille: famille.nom_famille,
            numero_famille: famille.numero_famille,
            chef_famille_nom: famille.chef_famille_nom
          } : null,
          recolteur: nomRecolteur ? { nom_complet: nomRecolteur } : null
        };
      });

      // Combiner les paiements membres et familles en une seule liste
      const tousPaiementsConfirmes = [
        ...paiementsAvecDetails.map(p => ({ ...p, type: 'membre' })),
        ...paiementsFamillesAvecDetails.map(p => ({ ...p, type: 'famille' }))
      ];
      
      // Trier par date de paiement (plus récent en premier)
      tousPaiementsConfirmes.sort((a, b) => {
        const dateA = new Date(a.date_paiement || a.created_at);
        const dateB = new Date(b.date_paiement || b.created_at);
        return dateB - dateA;
      });

      setPaiementsJournaliers(paiementsAvecDetails);
      setPaiementsFamillesJournaliers(paiementsFamillesAvecDetails);
      
      // Stocker aussi la liste combinée
      setPaiementsJournaliersCombines(tousPaiementsConfirmes);
    } catch (error) {
      console.error('Erreur lors du chargement du rapport journalier:', error);
      notificationService.error('Erreur', 'Erreur lors du chargement du rapport journalier');
      // Initialiser les listes vides en cas d'erreur
      setPaiementsJournaliers([]);
      setPaiementsFamillesJournaliers([]);
      setPaiementsJournaliersCombines([]);
    } finally {
      setLoadingRapport(false);
    }
  };

  // Calcul des statistiques par récolteur
  const getStatistiquesRecolteurs = useCallback((typeRapport, dateFiltre) => {
    let paiementsAAnalyser = [...paiementsJournaliersCombines];
    
    // Filtrer par date si rapport journalier
    if (typeRapport === 'journalier' && dateFiltre) {
      paiementsAAnalyser = paiementsAAnalyser.filter(p => {
        const datePaiement = p.date_paiement || p.created_at;
        if (!datePaiement) return false;
        const dateP = new Date(datePaiement).toISOString().split('T')[0];
        return dateP === dateFiltre;
      });
    }
    
    // Grouper par récolteur
    const statsParRecolteur = {};
    
    paiementsAAnalyser.forEach(paiement => {
      const nomRecolteur = paiement.recolteur?.nom_complet || 'Non assigné';
      
      if (!statsParRecolteur[nomRecolteur]) {
        statsParRecolteur[nomRecolteur] = {
          nom: nomRecolteur,
          nombreMembres: 0,
          nombreFamilles: 0,
          totalMembres: 0,
          totalFamilles: 0,
          paiements: []
        };
      }
      
      if (paiement.type === 'membre') {
        statsParRecolteur[nomRecolteur].nombreMembres++;
        statsParRecolteur[nomRecolteur].totalMembres += parseFloat(paiement.montant || 0);
      } else {
        statsParRecolteur[nomRecolteur].nombreFamilles++;
        statsParRecolteur[nomRecolteur].totalFamilles += parseFloat(paiement.montant || 0);
      }
      
      statsParRecolteur[nomRecolteur].paiements.push(paiement);
    });
    
    // Convertir en tableau et trier par total décroissant
    return Object.values(statsParRecolteur).sort((a, b) => 
      (b.totalMembres + b.totalFamilles) - (a.totalMembres + a.totalFamilles)
    );
  }, [paiementsJournaliersCombines]);

  // Fonction d'impression du rapport des récolteurs
  const handlePrintRapportRecolteurs = () => {
    const stats = getStatistiquesRecolteurs(rapportType, dateRapport);
    const dateFormatee = new Date(dateRapport).toLocaleDateString('fr-FR', { 
      weekday: 'long', 
      day: '2-digit', 
      month: 'long', 
      year: 'numeric' 
    });
    
    const totalGeneral = stats.reduce((sum, s) => sum + s.totalMembres + s.totalFamilles, 0);
    const totalMembres = stats.reduce((sum, s) => sum + s.nombreMembres, 0);
    const totalFamilles = stats.reduce((sum, s) => sum + s.nombreFamilles, 0);
    
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Rapport des Récolteurs - ${rapportType === 'journalier' ? 'Journalier' : 'Global'}</title>
        <style>
          body { 
            font-family: Arial, sans-serif; 
            padding: 20px;
            font-size: 12px;
          }
          .header { 
            text-align: center; 
            margin-bottom: 30px;
            border-bottom: 2px solid #333;
            padding-bottom: 20px;
          }
          .header h1 { 
            margin: 0; 
            color: #2c3e50;
            font-size: 24px;
          }
          .header h2 { 
            margin: 10px 0 5px 0; 
            color: #667eea;
            font-size: 18px;
          }
          .header p { 
            margin: 5px 0; 
            color: #666;
          }
          .summary {
            display: flex;
            justify-content: space-around;
            margin-bottom: 30px;
            padding: 15px;
            background: #f8f9fa;
            border-radius: 8px;
          }
          .summary-item {
            text-align: center;
          }
          .summary-item .value {
            font-size: 24px;
            font-weight: bold;
            color: #667eea;
          }
          .summary-item .label {
            color: #666;
            font-size: 12px;
          }
          table { 
            width: 100%; 
            border-collapse: collapse; 
            margin-top: 20px;
          }
          th, td { 
            border: 1px solid #ddd; 
            padding: 10px 8px; 
            text-align: left;
          }
          th { 
            background-color: #667eea; 
            color: white;
            font-weight: bold;
          }
          tr:nth-child(even) { 
            background-color: #f8f9fa;
          }
          .text-right { text-align: right; }
          .text-center { text-align: center; }
          .total-row { 
            font-weight: bold; 
            background-color: #e3e7f1 !important;
          }
          .footer {
            margin-top: 40px;
            text-align: center;
            color: #666;
            font-size: 10px;
            border-top: 1px solid #ddd;
            padding-top: 20px;
          }
          .signature-area {
            margin-top: 60px;
            display: flex;
            justify-content: space-between;
          }
          .signature-box {
            width: 200px;
            text-align: center;
          }
          .signature-line {
            border-top: 1px solid #333;
            margin-top: 50px;
            padding-top: 5px;
          }
          @media print {
            body { padding: 0; }
            .no-print { display: none; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>MUTU NDE ESPOIR ASBL</h1>
          <h2>Rapport des Récolteurs - ${rapportType === 'journalier' ? 'Journalier' : 'Global'}</h2>
          ${rapportType === 'journalier' ? `<p><strong>Date:</strong> ${dateFormatee}</p>` : '<p><strong>Période:</strong> Toutes les données</p>'}
          <p><strong>Imprimé le:</strong> ${new Date().toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
        </div>
        
        <div class="summary">
          <div class="summary-item">
            <div class="value">${stats.length}</div>
            <div class="label">Récolteurs</div>
          </div>
          <div class="summary-item">
            <div class="value">${totalMembres}</div>
            <div class="label">Paiements Membres</div>
          </div>
          <div class="summary-item">
            <div class="value">${totalFamilles}</div>
            <div class="label">Paiements Familles</div>
          </div>
          <div class="summary-item">
            <div class="value">${totalGeneral.toLocaleString('fr-FR')} CDF</div>
            <div class="label">Total Général</div>
          </div>
        </div>
        
        <table>
          <thead>
            <tr>
              <th class="text-center">#</th>
              <th>Récolteur</th>
              <th class="text-center">Membres Récoltés</th>
              <th class="text-right">Montant Membres</th>
              <th class="text-center">Familles Récoltées</th>
              <th class="text-right">Montant Familles</th>
              <th class="text-right">Total</th>
            </tr>
          </thead>
          <tbody>
            ${stats.map((stat, index) => `
              <tr>
                <td class="text-center">${index + 1}</td>
                <td><strong>${stat.nom}</strong></td>
                <td class="text-center">${stat.nombreMembres}</td>
                <td class="text-right">${stat.totalMembres.toLocaleString('fr-FR')} CDF</td>
                <td class="text-center">${stat.nombreFamilles}</td>
                <td class="text-right">${stat.totalFamilles.toLocaleString('fr-FR')} CDF</td>
                <td class="text-right"><strong>${(stat.totalMembres + stat.totalFamilles).toLocaleString('fr-FR')} CDF</strong></td>
              </tr>
            `).join('')}
            <tr class="total-row">
              <td colspan="2" class="text-right"><strong>TOTAL</strong></td>
              <td class="text-center"><strong>${totalMembres}</strong></td>
              <td class="text-right"><strong>${stats.reduce((sum, s) => sum + s.totalMembres, 0).toLocaleString('fr-FR')} CDF</strong></td>
              <td class="text-center"><strong>${totalFamilles}</strong></td>
              <td class="text-right"><strong>${stats.reduce((sum, s) => sum + s.totalFamilles, 0).toLocaleString('fr-FR')} CDF</strong></td>
              <td class="text-right"><strong>${totalGeneral.toLocaleString('fr-FR')} CDF</strong></td>
            </tr>
          </tbody>
        </table>
        
        <div class="signature-area">
          <div class="signature-box">
            <div class="signature-line">Le Financier</div>
          </div>
          <div class="signature-box">
            <div class="signature-line">Le Responsable</div>
          </div>
        </div>
        
        <div class="footer">
          <p>Document généré automatiquement par le système de gestion MUTU NDE ESPOIR ASBL</p>
        </div>
      </body>
      </html>
    `;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 500);
  };

  // Fonctions supprimées - la confirmation des paiements se fait maintenant sur des pages séparées
  // (ConfirmationPaiementsFinances et ConfirmationPaiementsFamilleFinances)
  
  /* Anciennes fonctions supprimées - redirige vers /finances/paiement-membre/:membreId et /finances/paiement-famille/:familleId
  const loadCotisationsMembre = async (membreId) => {
    try {
      // Vérifier que membreId est valide
      if (!membreId || isNaN(parseInt(membreId))) {
        console.error('❌ Erreur: membreId invalide:', membreId);
        notificationService.error('Erreur', 'ID de membre invalide');
        return;
      }
      
      console.log('📊 Chargement des cotisations pour membre:', membreId);
      
      const [cotisationsRes, montantsRes] = await Promise.all([
        getCotisationsByMembre(membreId),
        getMontantsMensuels()
      ]);
      
      const cotisations = cotisationsRes.data || [];
      const montants = montantsRes.data || [];
      
      console.log('✅ Cotisations chargées:', cotisations.length, 'cotisations trouvées');
      
      // Trouver le membre pour obtenir sa date d'adhésion
      const membre = membres.find(m => m.id === membreId);
      
      // Organiser par mois (identique à ConfirmationPaiements)
      // Grouper les cotisations par mois pour l'affichage
      const cotisationsParMoisMap = {};
      cotisations.forEach(cot => {
        const key = `${cot.annee}-${cot.mois}`;
        if (!cotisationsParMoisMap[key]) {
          cotisationsParMoisMap[key] = [];
        }
        cotisationsParMoisMap[key].push(cot);
      });
      
      // Grouper les cotisations par mois pour l'affichage (identique à ConfirmationPaiements)
      const cotisationsGrouped = {};
      cotisations.forEach(cotisation => {
        const key = `${cotisation.annee}-${cotisation.mois}`;
        if (!cotisationsGrouped[key]) {
          cotisationsGrouped[key] = [];
        }
        cotisationsGrouped[key].push(cotisation);
      });
      setCotisationsParMois(cotisationsGrouped);
      
      // Générer la liste de tous les mois (identique à ConfirmationPaiements)
      const moisUniques = new Map();
      
      // D'abord, ajouter tous les mois qui ont des cotisations
      cotisations.forEach(cotisation => {
        const key = `${cotisation.annee}-${cotisation.mois}`;
        if (!moisUniques.has(key)) {
          moisUniques.set(key, {
            annee: cotisation.annee,
            mois: cotisation.mois,
            cotisations: []
          });
        }
        moisUniques.get(key).cotisations.push(cotisation);
      });
      
      // Ensuite, ajouter TOUS les mois qui ont un montant mensuel fixé, même les mois futurs
      // MAIS seulement à partir du mois/année de la date d'adhésion du membre
      if (membre && membre.date_adhesion) {
        const dateAdhesion = new Date(membre.date_adhesion);
        const anneeAdhesion = dateAdhesion.getFullYear();
        const moisAdhesion = dateAdhesion.getMonth() + 1; // getMonth() retourne 0-11
        
        montants.forEach(montant => {
          // Vérifier si le mois/année est après ou égal au mois/année d'adhésion
          // Inclure tous les mois futurs avec un montant fixé, sans restriction de date actuelle
          const moisApresAdhesion = montant.annee > anneeAdhesion || 
                                    (montant.annee === anneeAdhesion && montant.mois >= moisAdhesion);
          
          if (moisApresAdhesion) {
            const key = `${montant.annee}-${montant.mois}`;
            if (!moisUniques.has(key)) {
              // Ce mois a un montant fixé mais pas encore de cotisations pour ce membre
              // Inclure même les mois futurs
              moisUniques.set(key, {
                annee: montant.annee,
                mois: montant.mois,
                cotisations: [] // Pas encore de cotisations créées
              });
            }
          }
        });
      } else {
        // Si pas de date d'adhésion, inclure tous les mois avec un montant fixé (y compris les mois futurs)
        montants.forEach(montant => {
          const key = `${montant.annee}-${montant.mois}`;
          if (!moisUniques.has(key)) {
            moisUniques.set(key, {
              annee: montant.annee,
              mois: montant.mois,
              cotisations: []
            });
          }
        });
      }
      
      // Filtrer les mois pour ne garder que ceux à partir de la date d'adhésion
      // Mais inclure tous les mois futurs avec un montant fixé
      let moisList = Array.from(moisUniques.values());
      
      if (membre && membre.date_adhesion) {
        const dateAdhesion = new Date(membre.date_adhesion);
        const anneeAdhesion = dateAdhesion.getFullYear();
        const moisAdhesion = dateAdhesion.getMonth() + 1;
        
        moisList = moisList.filter(moisData => {
          // Inclure tous les mois à partir de la date d'adhésion, même les mois futurs
          return moisData.annee > anneeAdhesion || 
                 (moisData.annee === anneeAdhesion && moisData.mois >= moisAdhesion);
        });
      }
      
      // Trier (plus récent en premier)
      moisList.sort((a, b) => {
        if (a.annee !== b.annee) return b.annee - a.annee;
        return b.mois - a.mois;
      });
      
      console.log('📅 Mois uniques trouvés pour membre:', moisList.length);
      setTousLesMois(moisList);
    } catch (error) {
      console.error('❌ Erreur lors du chargement des cotisations:', error);
      console.error('❌ Détails de l\'erreur:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        statusText: error.response?.statusText,
        url: error.config?.url
      });
      
      // Afficher un message d'erreur plus informatif
      if (error.response?.status === 404) {
        notificationService.error('Erreur', 'Membre non trouvé ou cotisations non disponibles');
      } else if (error.response?.status === 403) {
        notificationService.error('Erreur', 'Accès refusé. Vous n\'avez pas les permissions nécessaires.');
      } else if (error.response?.status === 401) {
        notificationService.error('Erreur', 'Session expirée. Veuillez vous reconnecter.');
        // Rediriger vers la page de connexion
        setTimeout(() => {
          navigate('/login-finances');
        }, 2000);
      } else {
        notificationService.error('Erreur', `Erreur lors du chargement des cotisations: ${error.response?.data?.message || error.message || 'Erreur inconnue'}`);
      }
    }
  };
  
  const loadCotisationsImpayeesParMois = async (mois, annee, membreId) => {
    const key = `${annee}-${mois}`;
    setLoadingCotisationsParMois(prev => ({ ...prev, [key]: true }));
    try {
      // Vérifier que membreId est valide
      if (!membreId || isNaN(parseInt(membreId))) {
        console.error('❌ Erreur: membreId invalide dans loadCotisationsImpayeesParMois:', membreId);
        setCotisationsParMois(prev => ({ ...prev, [key]: [] }));
        return;
      }
      
      console.log(`📊 Chargement des cotisations impayées pour ${mois}/${annee}, membre: ${membreId}`);
      
      const response = await getCotisationsImpayeesByMois(mois, annee, membreId);
      // Filtrer uniquement les cotisations impayées (identique à ConfirmationPaiements)
      // Note: Cette fonction charge seulement les impayées pour le header
      // Les cotisations complètes (payées et impayées) sont dans moisData.cotisations
      const cotisationsImpayees = (response.data || []).filter(c => c.statut === 'impaye');
      console.log(`✅ ${cotisationsImpayees.length} cotisations impayées trouvées pour ${mois}/${annee}`);
      setCotisationsParMois(prev => ({ ...prev, [key]: cotisationsImpayees }));
    } catch (err) {
      console.error(`❌ Erreur lors du chargement des cotisations pour ${mois}/${annee}:`, err);
      console.error('❌ Détails de l\'erreur:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status,
        statusText: err.response?.statusText,
        url: err.config?.url
      });
      setCotisationsParMois(prev => ({ ...prev, [key]: [] }));
    } finally {
      setLoadingCotisationsParMois(prev => ({ ...prev, [key]: false }));
    }
  };
  
  const toggleMoisExpande = (mois, annee, membreId) => {
    const key = `${annee}-${mois}`;
    const isExpanded = moisExpande[key] || false;
    
    if (!isExpanded) {
      if (!cotisationsParMois[key]) {
        loadCotisationsImpayeesParMois(mois, annee, membreId);
      }
    }
    
    setMoisExpande(prev => ({ ...prev, [key]: !isExpanded }));
  };

  const loadCotisationsFamille = async (familleId) => {
    try {
      const [familleRes, cotisationsRes, montantsRes] = await Promise.all([
        getFamille(familleId),
        getCotisationsFamilles(familleId),
        getMontantsMensuelsFamilles()
      ]);
      
      const famille = familleRes.data;
      const cotisations = cotisationsRes.data || [];
      const montants = montantsRes.data || [];
      
      // Organiser par mois
      const cotisationsParMoisMap = {};
      cotisations.forEach(cot => {
        const key = `${cot.annee}-${cot.mois}`;
        if (!cotisationsParMoisMap[key]) {
          cotisationsParMoisMap[key] = [];
        }
        cotisationsParMoisMap[key].push(cot);
      });
      
      setCotisationsFamillesParMois(cotisationsParMoisMap);
      
      // Générer la liste de tous les mois (comme pour les membres et ConfirmationPaiementsFamille)
      const moisUniques = new Map();
      
      // D'abord, ajouter tous les mois qui ont des cotisations
      cotisations.forEach(cotisation => {
        const key = `${cotisation.annee}-${cotisation.mois}`;
        if (!moisUniques.has(key)) {
          moisUniques.set(key, {
            annee: cotisation.annee,
            mois: cotisation.mois,
            cotisations: []
          });
        }
        moisUniques.get(key).cotisations.push(cotisation);
      });
      
      // Ensuite, ajouter TOUS les mois qui ont un montant mensuel fixé
      // (même les mois futurs, comme dans ConfirmationPaiementsFamille)
      montants.forEach(montant => {
        const key = `${montant.annee}-${montant.mois}`;
        if (!moisUniques.has(key)) {
          moisUniques.set(key, {
            annee: montant.annee,
            mois: montant.mois,
            cotisations: []
          });
        }
      });
      
      // Trier (plus récent en premier)
      let moisList = Array.from(moisUniques.values());
      moisList.sort((a, b) => {
        if (a.annee !== b.annee) return b.annee - a.annee;
        return b.mois - a.mois;
      });
      
      console.log('📅 Mois uniques trouvés pour famille:', moisList.length);
      setTousLesMoisFamilles(moisList);
    } catch (error) {
      console.error('Erreur lors du chargement des cotisations famille:', error);
    }
  };
  */

  const handleOpenPaiementMembre = async (membre) => {
    // Vérifier que le membre a un ID valide
    if (!membre || !membre.id) {
      console.error('❌ Erreur: membre invalide dans handleOpenPaiementMembre:', membre);
      notificationService.error('Erreur', 'Impossible d\'identifier le membre.');
      return;
    }
    
    console.log('💰 Redirection vers la page de confirmation de paiement pour membre:', membre.id, membre.nom);
    
    // Rediriger vers la page dédiée au lieu d'ouvrir un modal
    navigate(`/finances/paiement-membre/${membre.id}`);
  };

  const handleOpenPaiementFamille = async (famille) => {
    // Vérifier que la famille a un ID valide
    if (!famille || !famille.id) {
      console.error('❌ Erreur: famille invalide dans handleOpenPaiementFamille:', famille);
      notificationService.error('Erreur', 'Impossible d\'identifier la famille.');
      return;
    }
    
    console.log('💰 Redirection vers la page de confirmation de paiement pour famille:', famille.id, famille.nom_famille);
    
    // Rediriger vers la page dédiée au lieu d'ouvrir un modal
    navigate(`/finances/paiement-famille/${famille.id}`);
  };

  /* Fonctions supprimées - non utilisées car la confirmation se fait sur pages séparées
  const handleConfirmerPaiementMembre = async (cotisation) => {
    // Vérifier si la cotisation est déjà payée (identique à ConfirmationPaiements)
    if (cotisation.statut === 'paye') {
      notificationService.warning('Déjà payée', 'Cette cotisation est déjà payée.');
      return;
    }

    const membreIdNum = parseInt(paiementMembre.id);
    if (!membreIdNum) {
      notificationService.error('Erreur', 'Impossible d\'identifier le membre.');
      return;
    }

    if (cotisation.membre_id && cotisation.membre_id !== membreIdNum) {
      notificationService.error('Erreur', 'Cette cotisation n\'appartient pas au membre sélectionné.');
      return;
    }

    const montant = parseFloat(cotisation.montant || 0);
    if (!montant) {
      notificationService.error('Erreur', 'Aucun montant défini pour cette cotisation');
      return;
    }

    const membreNom = `${paiementMembre.nom} ${paiementMembre.postnom || ''} ${paiementMembre.prenom}`;
    const messageConfirmation = `Confirmer le paiement de ${montant.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} CDF pour ${membreNom} - Semaine ${cotisation.semaine} de ${getNomMois(cotisation.mois)} ${cotisation.annee} ?`;

    if (!window.confirm(messageConfirmation)) {
      return;
    }

    try {
      let cotisationId = cotisation.id;
      
      // Si la cotisation n'existe pas encore (pas d'ID), la créer d'abord
      if (!cotisationId) {
        const montantMensuel = montantsMensuels.find(m => m.mois === cotisation.mois && m.annee === cotisation.annee);
        if (!montantMensuel) {
          notificationService.error('Erreur', 'Aucun montant mensuel trouvé pour ce mois.');
          return;
        }
        
        try {
          // Créer la cotisation
          const cotisationResponse = await createCotisation({
            membre_id: membreIdNum,
            mois: cotisation.mois,
            annee: cotisation.annee,
            semaine: cotisation.semaine,
            montant: montant,
            date_echeance: cotisation.date_echeance
          });
          
          console.log('✅ Réponse création cotisation:', cotisationResponse.data);
          
          // Vérifier la structure de la réponse
          if (cotisationResponse.data && cotisationResponse.data.cotisation) {
            cotisationId = cotisationResponse.data.cotisation.id;
          } else if (cotisationResponse.data && cotisationResponse.data.id) {
            cotisationId = cotisationResponse.data.id;
          } else {
            throw new Error('Structure de réponse inattendue lors de la création de la cotisation');
          }
        } catch (cotisationError) {
          console.error('❌ Erreur lors de la création de la cotisation:', cotisationError);
          const cotisationErrorMessage = cotisationError.response?.data?.message || cotisationError.message || 'Erreur lors de la création de la cotisation';
          notificationService.error('Erreur', cotisationErrorMessage);
          return;
        }
      }

      console.log('💰 Création du paiement pour cotisation ID:', cotisationId);
      
      const paiementData = {
        membre_id: membreIdNum,
        montant: montant,
        date_paiement: new Date().toISOString().split('T')[0],
        type_paiement: 'cotisation_mensuelle',
        cotisation_id: cotisationId,
        confirmed_by: 'finances'
      };

      const paiementResponse = await createPaiement(paiementData);

      console.log('✅ Paiement créé avec succès:', paiementResponse.data);
      console.log('📋 Données du paiement envoyées:', paiementData);
      console.log('📋 confirmed_by envoyé:', paiementData.confirmed_by);
      
      // Vérifier que le paiement a bien été créé avec confirmed_by
      if (paiementResponse.data && paiementResponse.data.paiement) {
        console.log('📋 Paiement créé avec confirmed_by:', paiementResponse.data.paiement.confirmed_by);
      } else if (paiementResponse.data) {
        console.log('📋 Paiement créé avec confirmed_by:', paiementResponse.data.confirmed_by);
      }
      
      notificationService.success('Paiement confirmé', `Le paiement de ${membreNom} a été confirmé avec succès.`);
      
      // Sauvegarder l'ID et les données du membre actuel avant de recharger
      const membreIdActuel = membreIdNum;
      const membreActuel = { ...paiementMembre }; // Copie pour éviter les problèmes de référence
      
      // Recharger les cotisations pour ce mois
      await loadCotisationsMembre(membreIdNum);
      
      // Recharger uniquement les montants mensuels (pas toute la liste des membres)
      // pour éviter de perdre la sélection du membre dans le modal
      try {
        const [montantsRes] = await Promise.all([
          getMontantsMensuels()
        ]);
        setMontantsMensuels(montantsRes.data || []);
      } catch (error) {
        console.error('Erreur lors du rechargement des montants:', error);
      }
      
      // Attendre un peu pour que le paiement soit bien enregistré en base
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Recharger les cotisations à nouveau pour avoir les données à jour
      await loadCotisationsMembre(membreIdActuel);
      
      // S'assurer que le membre sélectionné reste le même (garder la référence originale)
      // Ne pas recharger toute la liste des membres pour éviter de changer le modal
      if (paiementMembre && paiementMembre.id === membreActuel.id) {
        // Le membre est toujours le même, pas besoin de changer
        console.log('✅ Membre sélectionné conservé:', membreActuel.id);
      } else {
        // Si pour une raison quelconque le membre a changé, le restaurer
        setPaiementMembre(membreActuel);
        console.log('🔄 Membre sélectionné restauré:', membreActuel.id);
      }
      
      // Toujours recharger le rapport journalier pour avoir les données à jour
      console.log('🔄 Rechargement du rapport journalier après confirmation paiement membre...');
      await loadRapportJournalier();
    } catch (err) {
      console.error('❌ Erreur générale lors de la confirmation du paiement:', err);
      const errorMessage = err.response?.data?.message || err.response?.data?.error || err.message || 'Erreur lors de la confirmation du paiement';
      notificationService.error('Erreur', errorMessage);
    }
  };

  const handleConfirmerPaiementFamille = async (cotisation) => {
    const familleIdNum = parseInt(paiementFamille.id);
    if (!familleIdNum) {
      notificationService.error('Erreur', 'Impossible d\'identifier la famille.');
      return;
    }

    // Trouver le montant mensuel pour ce mois
    const montantMensuel = montantsMensuelsFamilles.find(m => m.mois === cotisation.mois && m.annee === cotisation.annee);
    if (!montantMensuel) {
      notificationService.error('Erreur', 'Aucun montant mensuel trouvé pour ce mois.');
      return;
    }

    const montant = parseFloat(cotisation.montant || montantMensuel.montant_mensuel || montantMensuel.montant || 0);
    if (!montant) {
      notificationService.error('Erreur', 'Aucun montant défini pour ce mois');
      return;
    }

    // Vérifier si une cotisation existe déjà pour ce mois
    const cotisationsExistantes = cotisationsFamillesParMois[`${cotisation.annee}-${cotisation.mois}`] || [];
    const cotisationExistante = cotisationsExistantes.find(c => c.statut === 'impaye');
    
    if (cotisationExistante && cotisationExistante.statut === 'paye') {
      notificationService.warning('Déjà payée', 'Cette cotisation est déjà payée.');
      return;
    }

    const familleNom = paiementFamille.nom_famille || 'Famille';
    const messageConfirmation = `Confirmer le paiement de ${montant.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} CDF pour ${familleNom} - ${getNomMois(cotisation.mois)} ${cotisation.annee} ?`;

    if (!window.confirm(messageConfirmation)) {
      return;
    }

    try {
      let cotisationId = cotisationExistante?.id || cotisation.id;
      
      // Si la cotisation n'existe pas encore (pas d'ID), la créer d'abord
      if (!cotisationId) {
        // Calculer la date d'échéance (fin du mois)
        const dateEcheance = new Date(cotisation.annee, cotisation.mois, 0); // Dernier jour du mois
        
        try {
          // Créer la cotisation mensuelle (sans semaine)
          const cotisationResponse = await createCotisationFamille({
            famille_id: familleIdNum,
            mois: cotisation.mois,
            annee: cotisation.annee,
            montant: montant,
            date_echeance: dateEcheance.toISOString().split('T')[0]
          });
          
          console.log('✅ Réponse création cotisation:', cotisationResponse.data);
          
          // Vérifier la structure de la réponse
          if (cotisationResponse.data && cotisationResponse.data.cotisation) {
            cotisationId = cotisationResponse.data.cotisation.id;
          } else if (cotisationResponse.data && cotisationResponse.data.id) {
            cotisationId = cotisationResponse.data.id;
          } else {
            throw new Error('Structure de réponse inattendue lors de la création de la cotisation');
          }
        } catch (cotisationError) {
          console.error('❌ Erreur lors de la création de la cotisation:', cotisationError);
          const cotisationErrorMessage = cotisationError.response?.data?.message || cotisationError.message || 'Erreur lors de la création de la cotisation';
          notificationService.error('Erreur', cotisationErrorMessage);
          return;
        }
      }

      console.log('💰 Création du paiement pour cotisation ID:', cotisationId);
      
      const paiementData = {
        famille_id: familleIdNum,
        montant: montant,
        date_paiement: new Date().toISOString().split('T')[0],
        type_paiement: 'cotisation_famille',
        cotisation_ids: [cotisationId],
        confirmed_by: 'finances'
      };

      const paiementResponse = await createPaiementFamille(paiementData);

      console.log('✅ Paiement créé avec succès:', paiementResponse.data);
      console.log('📋 Données du paiement famille envoyées:', paiementData);
      console.log('📋 confirmed_by envoyé:', paiementData.confirmed_by);
      
      // Vérifier que le paiement a bien été créé avec confirmed_by
      if (paiementResponse.data && paiementResponse.data.paiement) {
        console.log('📋 Paiement famille créé avec confirmed_by:', paiementResponse.data.paiement.confirmed_by);
      } else if (paiementResponse.data) {
        console.log('📋 Paiement famille créé avec confirmed_by:', paiementResponse.data.confirmed_by);
      }
      
      notificationService.success('Paiement confirmé', `Le paiement de ${familleNom} a été confirmé avec succès.`);
      
      // Sauvegarder l'ID et les données de la famille actuelle avant de recharger
      const familleIdActuel = familleIdNum;
      const familleActuelle = { ...paiementFamille }; // Copie pour éviter les problèmes de référence
      
      // Recharger les cotisations pour ce mois
      await loadCotisationsFamille(familleIdNum);
      
      // Recharger uniquement les montants mensuels familles (pas toute la liste)
      // pour éviter de perdre la sélection de la famille dans le modal
      try {
        const [montantsFamillesRes] = await Promise.all([
          getMontantsMensuelsFamilles()
        ]);
        setMontantsMensuelsFamilles(montantsFamillesRes.data || []);
      } catch (error) {
        console.error('Erreur lors du rechargement des montants familles:', error);
      }
      
      // Attendre un peu pour que le paiement soit bien enregistré en base
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Recharger les cotisations à nouveau pour avoir les données à jour
      await loadCotisationsFamille(familleIdActuel);
      
      // S'assurer que la famille sélectionnée reste la même (garder la référence originale)
      // Ne pas recharger toute la liste des familles pour éviter de changer le modal
      if (paiementFamille && paiementFamille.id === familleActuelle.id) {
        // La famille est toujours la même, pas besoin de changer
        console.log('✅ Famille sélectionnée conservée:', familleActuelle.id);
      } else {
        // Si pour une raison quelconque la famille a changé, la restaurer
        setPaiementFamille(familleActuelle);
        console.log('🔄 Famille sélectionnée restaurée:', familleActuelle.id);
      }
      
      // Toujours recharger le rapport journalier pour avoir les données à jour
      console.log('🔄 Rechargement du rapport journalier après confirmation paiement famille...');
      await loadRapportJournalier();
    } catch (err) {
      console.error('❌ Erreur générale lors de la confirmation du paiement:', err);
      const errorMessage = err.response?.data?.message || err.response?.data?.error || err.message || 'Erreur lors de la confirmation du paiement';
      notificationService.error('Erreur', errorMessage);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric' 
    });
  };

  const getNomMois = (mois) => {
    const moisNoms = ['', 'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 
                     'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
    return moisNoms[mois] || mois;
  };
  */

  // Filtrage des membres (recherche + exclusion des familles)
  const membresFiltres = membres.filter(membre => {
    // S'assurer qu'on exclut toujours les membres de famille même après le chargement initial
    const isMembreFamille = membre.type === 'famille' || 
                             membre.famille_id || 
                             (membre.numero_matricule && membre.numero_matricule.startsWith('FAM-'));
    if (isMembreFamille) {
      return false;
    }
    
    // Filtrage par recherche
    const searchLower = searchTerm.toLowerCase();
    return (
      membre.numero_matricule?.toLowerCase().includes(searchLower) ||
      membre.nom?.toLowerCase().includes(searchLower) ||
      membre.prenom?.toLowerCase().includes(searchLower) ||
      membre.telephone?.toLowerCase().includes(searchLower)
    );
  });

  // Filtrage des familles
  const famillesFiltrees = familles.filter(famille => {
    const searchLower = searchTermFamilles.toLowerCase();
    return (
      famille.numero_famille?.toString().includes(searchLower) ||
      famille.nom_famille?.toLowerCase().includes(searchLower) ||
      famille.chef_famille_nom?.toLowerCase().includes(searchLower) ||
      famille.telephone?.toLowerCase().includes(searchLower)
    );
  });

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentMembres = membresFiltres.slice(indexOfFirstItem, indexOfLastItem);
  const totalPagesMembres = Math.ceil(membresFiltres.length / itemsPerPage);

  const indexOfLastItemFamilles = currentPageFamilles * itemsPerPage;
  const indexOfFirstItemFamilles = indexOfLastItemFamilles - itemsPerPage;
  const currentFamilles = famillesFiltrees.slice(indexOfFirstItemFamilles, indexOfLastItemFamilles);
  const totalPagesFamilles = Math.ceil(famillesFiltrees.length / itemsPerPage);

  // Filtrage des paiements confirmés (rapport)
  const paiementsFiltres = paiementsJournaliersCombines.filter(paiement => {
    const searchLower = searchTermRapport.toLowerCase();
    if (!searchLower) return true;
    
    // Recherche par numéro de reçu
    if (paiement.numero_recu?.toLowerCase().includes(searchLower)) return true;
    
    // Recherche par bénéficiaire (membre ou famille)
    if (paiement.type === 'membre' && paiement.membre) {
      const nomComplet = `${paiement.membre.nom} ${paiement.membre.postnom || ''} ${paiement.membre.prenom || ''}`.toLowerCase();
      if (nomComplet.includes(searchLower)) return true;
      if (paiement.membre.numero_matricule?.toLowerCase().includes(searchLower)) return true;
    }
    if (paiement.type === 'famille' && paiement.famille) {
      if (paiement.famille.nom_famille?.toLowerCase().includes(searchLower)) return true;
      if (paiement.famille.numero_famille?.toString().includes(searchLower)) return true;
    }
    
    // Recherche par récolteur
    if (paiement.recolteur?.nom_complet?.toLowerCase().includes(searchLower)) return true;
    
    // Recherche par type (membre/famille)
    if (paiement.type?.toLowerCase().includes(searchLower)) return true;
    
    // Recherche par montant
    if (paiement.montant?.toString().includes(searchLower)) return true;
    
    return false;
  });

  // Pagination pour le rapport (10 lignes par page)
  const indexOfLastItemRapport = currentPageRapport * itemsPerPageRapport;
  const indexOfFirstItemRapport = indexOfLastItemRapport - itemsPerPageRapport;
  const currentPaiementsRapport = paiementsFiltres.slice(indexOfFirstItemRapport, indexOfLastItemRapport);
  const totalPagesRapport = Math.ceil(paiementsFiltres.length / itemsPerPageRapport);

  const handleLogout = () => {
    localStorage.removeItem('finances_token');
    localStorage.removeItem('finances_user');
    navigate('/login-finances');
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Chargement...</p>
      </div>
    );
  }

  return (
    <div className="dashboard-finances">
      <header className="dashboard-header">
        <div className="header-content">
          <div className="header-left">
            <img src="/logo.png" alt="Logo" className="header-logo" />
            <div>
              <h1>MUTU NDE ESPOIR ASBL</h1>
              <p>Interface Finances</p>
            </div>
          </div>
          <div className="header-right">
            <span>Bienvenue, {finances.nom_complet || finances.username}</span>
            <button onClick={handleLogout} className="btn btn-secondary">Déconnexion</button>
          </div>
        </div>
      </header>

      <div className="dashboard-tabs">
        <button 
          className={`tab ${activeTab === 'membres' ? 'active' : ''}`}
          onClick={() => setActiveTab('membres')}
        >
          👥 Membres ({membres.length})
        </button>
        <button 
          className={`tab ${activeTab === 'familles' ? 'active' : ''}`}
          onClick={() => setActiveTab('familles')}
        >
          👨‍👩‍👧‍👦 Familles ({familles.length})
        </button>
        <button 
          className={`tab ${activeTab === 'rapport' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('rapport');
            loadRapportJournalier();
          }}
        >
          📊 Rapport des Paiements
        </button>
      </div>

      <div className="dashboard-content">
        {activeTab === 'membres' && (
          <div className="tab-content">
            <div className="search-bar">
              <input
                type="text"
                placeholder="Rechercher par nom, matricule ou téléphone..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                className="search-input"
              />
            </div>

            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Matricule</th>
                    <th>Nom</th>
                    <th>Prénom</th>
                    <th>Téléphone</th>
                    <th>Date d'adhésion</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {currentMembres.length > 0 ? (
                    currentMembres.map((membre, index) => (
                      <tr key={membre.id}>
                        <td>{indexOfFirstItem + index + 1}</td>
                        <td>{membre.numero_matricule || '-'}</td>
                        <td>{membre.nom || '-'}</td>
                        <td>{membre.prenom || '-'}</td>
                        <td>{membre.telephone || '-'}</td>
                        <td>{membre.date_adhesion ? new Date(membre.date_adhesion).toLocaleDateString('fr-FR') : '-'}</td>
                        <td>
                          <button
                            className="btn btn-sm btn-info"
                            onClick={() => setSelectedMembreDetail(membre)}
                            style={{ marginRight: '5px' }}
                          >
                            👁️ Détails
                          </button>
                          <button
                            className="btn btn-sm btn-success"
                            onClick={() => handleOpenPaiementMembre(membre)}
                          >
                            💰 Paiement
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7" style={{ textAlign: 'center', padding: '20px' }}>
                        Aucun membre trouvé
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {totalPagesMembres > 1 && (
              <div className="pagination">
                <button
                  className="btn btn-sm"
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                >
                  Précédent
                </button>
                <span>Page {currentPage} sur {totalPagesMembres}</span>
                <button
                  className="btn btn-sm"
                  onClick={() => setCurrentPage(prev => Math.min(totalPagesMembres, prev + 1))}
                  disabled={currentPage === totalPagesMembres}
                >
                  Suivant
                </button>
              </div>
            )}
          </div>
        )}

        {activeTab === 'familles' && (
          <div className="tab-content">
            <div className="search-bar">
              <input
                type="text"
                placeholder="Rechercher par numéro, nom ou téléphone..."
                value={searchTermFamilles}
                onChange={(e) => {
                  setSearchTermFamilles(e.target.value);
                  setCurrentPageFamilles(1);
                }}
                className="search-input"
              />
            </div>

            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Numéro</th>
                    <th>Nom Famille</th>
                    <th>Chef de Famille</th>
                    <th>Téléphone</th>
                    <th>Commune</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {currentFamilles.length > 0 ? (
                    currentFamilles.map((famille, index) => (
                      <tr key={famille.id}>
                        <td>{indexOfFirstItemFamilles + index + 1}</td>
                        <td>{famille.numero_famille || '-'}</td>
                        <td>{famille.nom_famille || '-'}</td>
                        <td>{famille.chef_famille_nom || '-'}</td>
                        <td>{famille.telephone || '-'}</td>
                        <td>{famille.commune || '-'}</td>
                        <td>
                          <button
                            className="btn btn-sm btn-success"
                            onClick={() => handleOpenPaiementFamille(famille)}
                          >
                            💰 Paiement
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7" style={{ textAlign: 'center', padding: '20px' }}>
                        Aucune famille trouvée
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {totalPagesFamilles > 1 && (
              <div className="pagination">
                <button
                  className="btn btn-sm"
                  onClick={() => setCurrentPageFamilles(prev => Math.max(1, prev - 1))}
                  disabled={currentPageFamilles === 1}
                >
                  Précédent
                </button>
                <span>Page {currentPageFamilles} sur {totalPagesFamilles}</span>
                <button
                  className="btn btn-sm"
                  onClick={() => setCurrentPageFamilles(prev => Math.min(totalPagesFamilles, prev + 1))}
                  disabled={currentPageFamilles === totalPagesFamilles}
                >
                  Suivant
                </button>
              </div>
            )}
          </div>
        )}

        {activeTab === 'rapport' && (
          <div className="tab-content">
            <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '15px' }}>
              <div>
                <h2 style={{ marginBottom: '10px' }}>📊 Rapport des Paiements Confirmés par Finances</h2>
                <p style={{ color: '#666', fontSize: '14px' }}>
                  Liste de tous les paiements confirmés par le service Finances
                </p>
                <p style={{ color: '#999', fontSize: '12px', fontStyle: 'italic', marginTop: '5px' }}>
                  Ce rapport affiche uniquement les paiements confirmés via l'interface Finances.
                </p>
              </div>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                <button 
                  className="btn btn-primary"
                  onClick={loadRapportJournalier}
                  disabled={loadingRapport}
                  style={{ minWidth: '150px' }}
                >
                  {loadingRapport ? 'Actualisation...' : '🔄 Actualiser'}
                </button>
                <button 
                  className="btn btn-success"
                  onClick={() => setShowRapportRecolteurs(true)}
                  style={{ minWidth: '200px' }}
                >
                  🖨️ Rapport Récolteurs
                </button>
              </div>
            </div>

            {loadingRapport ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <div className="spinner"></div>
                <p>Chargement du rapport...</p>
              </div>
            ) : (
              <div>
                {/* Statistiques */}
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
                  gap: '20px',
                  marginBottom: '30px'
                }}>
                  <div className="card" style={{ 
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    padding: '20px',
                    borderRadius: '8px'
                  }}>
                    <div style={{ fontSize: '14px', opacity: 0.9, marginBottom: '5px' }}>Paiements Membres</div>
                    <div style={{ fontSize: '32px', fontWeight: 'bold' }}>{paiementsJournaliers.length}</div>
                    <div style={{ fontSize: '14px', marginTop: '10px' }}>
                      Total: {paiementsJournaliers.reduce((sum, p) => sum + parseFloat(p.montant || 0), 0).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} CDF
                    </div>
                  </div>
                  <div className="card" style={{ 
                    background: 'linear-gradient(135deg, #28a745 0%, #20c997 100%)',
                    color: 'white',
                    padding: '20px',
                    borderRadius: '8px'
                  }}>
                    <div style={{ fontSize: '14px', opacity: 0.9, marginBottom: '5px' }}>Paiements Familles</div>
                    <div style={{ fontSize: '32px', fontWeight: 'bold' }}>{paiementsFamillesJournaliers.length}</div>
                    <div style={{ fontSize: '14px', marginTop: '10px' }}>
                      Total: {paiementsFamillesJournaliers.reduce((sum, p) => sum + parseFloat(p.montant || 0), 0).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} CDF
                    </div>
                  </div>
                  <div className="card" style={{ 
                    background: 'linear-gradient(135deg, #17a2b8 0%, #138496 100%)',
                    color: 'white',
                    padding: '20px',
                    borderRadius: '8px'
                  }}>
                    <div style={{ fontSize: '14px', opacity: 0.9, marginBottom: '5px' }}>Total Général</div>
                    <div style={{ fontSize: '32px', fontWeight: 'bold' }}>
                      {paiementsJournaliers.length + paiementsFamillesJournaliers.length}
                    </div>
                    <div style={{ fontSize: '14px', marginTop: '10px' }}>
                      Total: {(paiementsJournaliers.reduce((sum, p) => sum + parseFloat(p.montant || 0), 0) + 
                              paiementsFamillesJournaliers.reduce((sum, p) => sum + parseFloat(p.montant || 0), 0))
                              .toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} CDF
                    </div>
                  </div>
                </div>

                {/* Liste combinée des paiements */}
                <div className="card">
                  <h3 style={{ marginBottom: '20px', color: '#2c3e50' }}>📋 Liste des Paiements Confirmés par Finances ({paiementsFiltres.length})</h3>
                  
                  {/* Barre de recherche */}
                  <div className="search-bar" style={{ marginBottom: '20px' }}>
                    <input
                      type="text"
                      placeholder="Rechercher par numéro reçu, bénéficiaire, matricule, récolteur, montant..."
                      value={searchTermRapport}
                      onChange={(e) => {
                        setSearchTermRapport(e.target.value);
                        setCurrentPageRapport(1);
                      }}
                      className="search-input"
                      style={{ width: '100%', maxWidth: '500px' }}
                    />
                    {searchTermRapport && (
                      <span style={{ marginLeft: '10px', color: '#666', fontSize: '14px' }}>
                        {paiementsFiltres.length} résultat(s) trouvé(s)
                      </span>
                    )}
                  </div>
                  
                  {paiementsFiltres.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                      <p>{searchTermRapport ? 'Aucun paiement trouvé pour cette recherche' : 'Aucun paiement confirmé par le service finances'}</p>
                    </div>
                  ) : (
                    <div className="table-container">
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>Date</th>
                            <th>Numéro Reçu</th>
                            <th>Type</th>
                            <th>Bénéficiaire</th>
                            <th>Identifiant</th>
                            <th>Récolteur</th>
                            <th>Montant</th>
                            <th>Type Paiement</th>
                            <th>Semaine</th>
                          </tr>
                        </thead>
                        <tbody>
                          {currentPaiementsRapport.map((paiement, index) => (
                            <tr key={paiement.id}>
                              <td>{indexOfFirstItemRapport + index + 1}</td>
                              <td style={{ whiteSpace: 'nowrap' }}>
                                {paiement.date_paiement 
                                  ? new Date(paiement.date_paiement).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })
                                  : (paiement.created_at 
                                      ? new Date(paiement.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })
                                      : '-'
                                    )
                                }
                              </td>
                              <td><strong>{paiement.numero_recu || '-'}</strong></td>
                              <td>
                                {paiement.type === 'membre' ? (
                                  <span style={{ 
                                    padding: '4px 8px', 
                                    backgroundColor: '#667eea', 
                                    color: 'white', 
                                    borderRadius: '4px',
                                    fontSize: '12px',
                                    fontWeight: 'bold'
                                  }}>
                                    👤 Membre
                                  </span>
                                ) : (
                                  <span style={{ 
                                    padding: '4px 8px', 
                                    backgroundColor: '#28a745', 
                                    color: 'white', 
                                    borderRadius: '4px',
                                    fontSize: '12px',
                                    fontWeight: 'bold'
                                  }}>
                                    👨‍👩‍👧‍👦 Famille
                                  </span>
                                )}
                              </td>
                              <td>
                                {paiement.type === 'membre' ? (
                                  paiement.membre 
                                    ? `${paiement.membre.nom} ${paiement.membre.postnom || ''} ${paiement.membre.prenom || ''}`.trim()
                                    : 'Membre inconnu'
                                ) : (
                                  paiement.famille 
                                    ? paiement.famille.nom_famille
                                    : 'Famille inconnue'
                                )}
                              </td>
                              <td>
                                {paiement.type === 'membre' 
                                  ? (paiement.membre?.numero_matricule || '-')
                                  : (paiement.famille?.numero_famille || '-')
                                }
                              </td>
                              <td>
                                {paiement.recolteur ? (
                                  <span style={{ 
                                    padding: '3px 8px', 
                                    backgroundColor: '#17a2b8', 
                                    color: 'white', 
                                    borderRadius: '4px',
                                    fontSize: '12px'
                                  }}>
                                    {paiement.recolteur.nom_complet}
                                  </span>
                                ) : (
                                  <span style={{ color: '#999', fontStyle: 'italic' }}>-</span>
                                )}
                              </td>
                              <td style={{ fontWeight: 'bold', color: '#28a745' }}>
                                {parseFloat(paiement.montant || 0).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} CDF
                              </td>
                              <td>
                                {paiement.type === 'membre' ? (
                                  paiement.type_paiement === 'cotisation_mensuelle' ? 'Cotisation mensuelle' :
                                  paiement.type_paiement === 'cotisation_initial' ? 'Cotisation initiale' :
                                  'Autre'
                                ) : (
                                  paiement.type_paiement === 'cotisation_famille' ? 'Cotisation famille' : 'Autre'
                                )}
                              </td>
                              <td>{paiement.semaine || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  
                  {/* Pagination */}
                  {totalPagesRapport > 1 && (
                    <div className="pagination" style={{ marginTop: '20px' }}>
                      <button
                        className="btn btn-sm"
                        onClick={() => setCurrentPageRapport(1)}
                        disabled={currentPageRapport === 1}
                        title="Première page"
                      >
                        ⏮️
                      </button>
                      <button
                        className="btn btn-sm"
                        onClick={() => setCurrentPageRapport(prev => Math.max(1, prev - 1))}
                        disabled={currentPageRapport === 1}
                      >
                        Précédent
                      </button>
                      <span style={{ margin: '0 15px' }}>
                        Page {currentPageRapport} sur {totalPagesRapport} ({paiementsFiltres.length} paiements)
                      </span>
                      <button
                        className="btn btn-sm"
                        onClick={() => setCurrentPageRapport(prev => Math.min(totalPagesRapport, prev + 1))}
                        disabled={currentPageRapport === totalPagesRapport}
                      >
                        Suivant
                      </button>
                      <button
                        className="btn btn-sm"
                        onClick={() => setCurrentPageRapport(totalPagesRapport)}
                        disabled={currentPageRapport === totalPagesRapport}
                        title="Dernière page"
                      >
                        ⏭️
                      </button>
                    </div>
                  )}
                  
                  {/* Information sur la pagination */}
                  {paiementsFiltres.length > 0 && (
                    <div style={{ marginTop: '10px', color: '#666', fontSize: '13px', textAlign: 'center' }}>
                      Affichage de {indexOfFirstItemRapport + 1} à {Math.min(indexOfLastItemRapport, paiementsFiltres.length)} sur {paiementsFiltres.length} paiements
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {selectedMembreDetail && (
        <DetailMembre 
          membre={selectedMembreDetail} 
          onClose={() => setSelectedMembreDetail(null)} 
        />
      )}

      {/* Modal Rapport Récolteurs */}
      {showRapportRecolteurs && (
        <div className="modal-overlay" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div className="modal-content" style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '30px',
            width: '90%',
            maxWidth: '900px',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px' }}>
              <h2 style={{ margin: 0, color: '#2c3e50' }}>🖨️ Rapport des Récolteurs</h2>
              <button 
                onClick={() => setShowRapportRecolteurs(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '28px',
                  cursor: 'pointer',
                  color: '#666'
                }}
              >
                ×
              </button>
            </div>

            {/* Options du rapport */}
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
              gap: '20px',
              marginBottom: '25px',
              padding: '20px',
              backgroundColor: '#f8f9fa',
              borderRadius: '8px'
            }}>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#333' }}>
                  Type de rapport:
                </label>
                <select 
                  value={rapportType}
                  onChange={(e) => setRapportType(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '6px',
                    border: '1px solid #ddd',
                    fontSize: '14px'
                  }}
                >
                  <option value="journalier">📅 Rapport Journalier</option>
                  <option value="global">📊 Rapport Global (Toutes les données)</option>
                </select>
              </div>
              
              {rapportType === 'journalier' && (
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#333' }}>
                    Date du rapport:
                  </label>
                  <input 
                    type="date"
                    value={dateRapport}
                    onChange={(e) => setDateRapport(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px',
                      borderRadius: '6px',
                      border: '1px solid #ddd',
                      fontSize: '14px'
                    }}
                  />
                </div>
              )}
            </div>

            {/* Aperçu des statistiques */}
            <div style={{ marginBottom: '25px' }}>
              <h3 style={{ marginBottom: '15px', color: '#2c3e50' }}>
                📊 Aperçu - {rapportType === 'journalier' ? `Journée du ${new Date(dateRapport).toLocaleDateString('fr-FR')}` : 'Toutes les données'}
              </h3>
              
              {(() => {
                const stats = getStatistiquesRecolteurs(rapportType, dateRapport);
                const totalGeneral = stats.reduce((sum, s) => sum + s.totalMembres + s.totalFamilles, 0);
                const totalMembres = stats.reduce((sum, s) => sum + s.nombreMembres, 0);
                const totalFamilles = stats.reduce((sum, s) => sum + s.nombreFamilles, 0);
                
                return (
                  <>
                    {/* Résumé */}
                    <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', 
                      gap: '15px',
                      marginBottom: '20px'
                    }}>
                      <div style={{ 
                        padding: '15px', 
                        backgroundColor: '#667eea', 
                        color: 'white', 
                        borderRadius: '8px',
                        textAlign: 'center'
                      }}>
                        <div style={{ fontSize: '24px', fontWeight: 'bold' }}>{stats.length}</div>
                        <div style={{ fontSize: '12px', opacity: 0.9 }}>Récolteurs</div>
                      </div>
                      <div style={{ 
                        padding: '15px', 
                        backgroundColor: '#28a745', 
                        color: 'white', 
                        borderRadius: '8px',
                        textAlign: 'center'
                      }}>
                        <div style={{ fontSize: '24px', fontWeight: 'bold' }}>{totalMembres}</div>
                        <div style={{ fontSize: '12px', opacity: 0.9 }}>Paiements Membres</div>
                      </div>
                      <div style={{ 
                        padding: '15px', 
                        backgroundColor: '#17a2b8', 
                        color: 'white', 
                        borderRadius: '8px',
                        textAlign: 'center'
                      }}>
                        <div style={{ fontSize: '24px', fontWeight: 'bold' }}>{totalFamilles}</div>
                        <div style={{ fontSize: '12px', opacity: 0.9 }}>Paiements Familles</div>
                      </div>
                      <div style={{ 
                        padding: '15px', 
                        backgroundColor: '#ffc107', 
                        color: '#333', 
                        borderRadius: '8px',
                        textAlign: 'center'
                      }}>
                        <div style={{ fontSize: '20px', fontWeight: 'bold' }}>{totalGeneral.toLocaleString('fr-FR')}</div>
                        <div style={{ fontSize: '12px' }}>Total CDF</div>
                      </div>
                    </div>

                    {/* Tableau des récolteurs */}
                    {stats.length > 0 ? (
                      <div style={{ maxHeight: '300px', overflow: 'auto' }}>
                        <table className="data-table" style={{ width: '100%' }}>
                          <thead>
                            <tr>
                              <th>#</th>
                              <th>Récolteur</th>
                              <th style={{ textAlign: 'center' }}>Membres</th>
                              <th style={{ textAlign: 'right' }}>Montant</th>
                              <th style={{ textAlign: 'center' }}>Familles</th>
                              <th style={{ textAlign: 'right' }}>Montant</th>
                              <th style={{ textAlign: 'right' }}>Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {stats.map((stat, index) => (
                              <tr key={stat.nom}>
                                <td>{index + 1}</td>
                                <td><strong>{stat.nom}</strong></td>
                                <td style={{ textAlign: 'center' }}>{stat.nombreMembres}</td>
                                <td style={{ textAlign: 'right' }}>{stat.totalMembres.toLocaleString('fr-FR')} CDF</td>
                                <td style={{ textAlign: 'center' }}>{stat.nombreFamilles}</td>
                                <td style={{ textAlign: 'right' }}>{stat.totalFamilles.toLocaleString('fr-FR')} CDF</td>
                                <td style={{ textAlign: 'right', fontWeight: 'bold', color: '#28a745' }}>
                                  {(stat.totalMembres + stat.totalFamilles).toLocaleString('fr-FR')} CDF
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div style={{ textAlign: 'center', padding: '40px', color: '#666', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                        <p>Aucun paiement trouvé pour cette période</p>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>

            {/* Boutons d'action */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '15px', borderTop: '1px solid #eee', paddingTop: '20px' }}>
              <button 
                className="btn btn-secondary"
                onClick={() => setShowRapportRecolteurs(false)}
                style={{ minWidth: '120px' }}
              >
                Fermer
              </button>
              <button 
                className="btn btn-success"
                onClick={handlePrintRapportRecolteurs}
                style={{ minWidth: '180px' }}
                disabled={getStatistiquesRecolteurs(rapportType, dateRapport).length === 0}
              >
                🖨️ Imprimer le Rapport
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default DashboardFinances;
